<template>
  <q-page class="lesson-page">
    <div class="lesson-container">
      <!-- Loading state -->
      <template v-if="loading">
        <div class="skeleton-breadcrumb">
          <q-skeleton type="text" width="300px" height="14px" dark />
        </div>
        <div class="skeleton-header">
          <q-skeleton type="circle" size="40px" dark />
          <div style="flex: 1;">
            <q-skeleton type="text" width="350px" height="24px" dark />
            <q-skeleton type="text" width="200px" height="14px" dark style="margin-top: 8px;" />
          </div>
        </div>
        <q-skeleton type="rect" height="400px" dark class="skeleton-content" />
      </template>

      <!-- Error state -->
      <div v-else-if="error" class="error-card">
        <q-icon name="error_outline" size="48px" :style="{ color: 'var(--oa-danger)' }" />
        <p class="error-text">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          :style="{ background: 'var(--oa-gold-full)', color: 'var(--color-white-cap)', fontWeight: '600', borderRadius: '8px' }"
          @click="fetchData"
        />
      </div>

      <!-- Lesson content -->
      <template v-else-if="lesson">
        <!-- Breadcrumb -->
        <div class="lesson-breadcrumb">
          <router-link :to="`/learn/${courseId}`" class="breadcrumb-link">
            {{ courseTitle }}
          </router-link>
          <span class="breadcrumb-sep">/</span>
          <span class="breadcrumb-unit">{{ unitTitle }}</span>
          <span class="breadcrumb-sep">/</span>
          <span class="breadcrumb-current">Lección {{ lessonNumber }} de {{ totalInUnit }}</span>
        </div>

        <!-- Hero / cover image -->
        <div v-if="lesson.coverImageUrl" class="lesson-cover">
          <img :src="lesson.coverImageUrl" :alt="lesson.title" class="lesson-cover-img" />
          <div class="lesson-cover-overlay" />
        </div>

        <!-- Header -->
        <div class="lesson-header">
          <div class="lesson-header-top">
            <div class="lesson-number-badge">
              {{ lessonNumber }}
            </div>
            <div class="lesson-header-info">
              <p class="lesson-eyebrow">{{ unitTitle }}</p>
              <h1 class="lesson-title">{{ lesson.title }}</h1>
            </div>
          </div>

          <!-- Summary / lead paragraph -->
          <p v-if="lesson.summary" class="lesson-summary">
            {{ lesson.summary }}
          </p>

          <div class="lesson-meta">
            <span v-if="isCompleted" class="badge-completed">
              <q-icon name="check_circle" size="11px" />
              Completada
            </span>
            <span class="meta-type">
              <q-icon :name="lesson.contentType === 'video' ? 'play_circle' : 'menu_book'" size="13px" />
              {{ lesson.contentType === 'video' ? 'Video' : 'Lectura' }}
            </span>
            <span v-if="lesson.estimatedMinutes" class="meta-time">
              <q-icon name="schedule" size="12px" />
              {{ lesson.estimatedMinutes }} min
            </span>
          </div>

          <!-- Unit progress bar -->
          <div class="unit-progress-row">
            <div class="unit-progress-track">
              <div
                class="unit-progress-fill"
                :style="{ width: unitProgressPct + '%' }"
              />
            </div>
            <span class="unit-progress-text">
              {{ completedInUnit }} de {{ totalInUnit }} completadas
            </span>
          </div>
        </div>

        <!-- Featured video -->
        <div v-if="lesson.videoUrl" class="lesson-video">
          <div class="lesson-video-frame">
            <video
              v-if="isDirectVideo"
              :src="lesson.videoUrl"
              controls
              preload="metadata"
              class="lesson-video-el"
            />
            <iframe
              v-else
              :src="embeddedVideoUrl"
              class="lesson-video-iframe"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            />
          </div>
          <p class="lesson-video-caption">
            <q-icon name="play_circle" size="13px" :style="{ color: 'var(--oa-gold-full)' }" />
            Video de la lección
          </p>
        </div>

        <!-- Content card -->
        <article class="content-card">
          <LessonRenderer
            :rich-content="(lesson.richContent as ContentBlock[] | null)"
            :text-content="(lesson.textContent as string | null)"
            :lesson-id="lessonId"
          />
        </article>

        <!-- Attachments -->
        <div v-if="lesson.attachments && lesson.attachments.length > 0" class="attachments-section">
          <h3 class="attachments-title">
            <q-icon name="attach_file" size="18px" :style="{ color: 'var(--oa-gold-full)' }" />
            Material complementario
          </h3>
          <ul class="attachments-list">
            <li v-for="att in lesson.attachments" :key="att.url" class="attachment-item">
              <a :href="att.url" target="_blank" rel="noopener noreferrer" class="attachment-link">
                <q-icon name="description" size="18px" />
                <span>{{ att.name }}</span>
                <q-icon name="open_in_new" size="14px" class="attachment-ext" />
              </a>
            </li>
          </ul>
        </div>

        <!-- Complete button -->
        <div class="complete-section">
          <q-btn
            v-if="!isCompleted"
            no-caps
            unelevated
            :loading="completing"
            label="Completar lección"
            :style="{
              background: 'var(--oa-gold-full)',
              color: 'var(--color-white-cap)',
              fontWeight: '600',
              borderRadius: '12px',
              padding: '14px 28px',
              fontSize: '15px',
            }"
            @click="handleComplete"
          />
          <span v-else class="completed-label">
            <q-icon name="check_circle" size="20px" :style="{ color: 'var(--oa-success)' }" />
            Completada
          </span>
        </div>

        <!-- Navigation footer -->
        <div class="nav-footer">
          <router-link
            v-if="prevLesson"
            :to="`/learn/${courseId}/lesson/${prevLesson.id}`"
            class="nav-link nav-prev"
          >
            <span class="nav-arrow">&larr;</span>
            <span class="nav-label-full">{{ prevLesson.title }}</span>
            <span class="nav-label-short">Anterior</span>
          </router-link>
          <div v-else />

          <router-link
            v-if="nextLesson"
            :to="nextLessonHref"
            class="nav-link nav-next"
          >
            <span class="nav-label-full">{{ nextLesson.title }}</span>
            <span class="nav-label-short">Siguiente</span>
            <span class="nav-arrow">&rarr;</span>
          </router-link>
          <div v-else />
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { api } from 'boot/axios';
import LessonRenderer from 'src/components/lesson/LessonRenderer.vue';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ContentBlock = Record<string, any>;

interface LessonData {
  id: string;
  title: string;
  contentType: string;
  orderIndex: number;
  estimatedMinutes: number | null;
  xpReward: number;
  richContent: ContentBlock[] | null;
  textContent: string | null;
  summary: string | null;
  coverImageUrl: string | null;
  videoUrl: string | null;
  videoDurationSec: number | null;
  attachments: { name: string; url: string }[] | null;
}

interface SiblingLesson {
  id: string;
  title: string;
  contentType: string;
  orderIndex: number;
}

interface UnitData {
  id: string;
  title: string;
  lessons: SiblingLesson[];
}

interface CourseData {
  id: string;
  title: string;
  slug: string;
  units: UnitData[];
}

const $q = useQuasar();
const route = useRoute();
const router = useRouter();
const courseId = computed(() => route.params.courseId as string);
const lessonId = computed(() => route.params.lessonId as string);

const loading = ref(true);
const error = ref<string | null>(null);
const lesson = ref<LessonData | null>(null);
const courseTitle = ref('');
const unitTitle = ref('');
const unitLessons = ref<SiblingLesson[]>([]);
const completedLessonIds = ref<Set<string>>(new Set());
const isCompleted = ref(false);
const completing = ref(false);

const lessonNumber = computed(() => {
  const idx = unitLessons.value.findIndex((l) => l.id === lessonId.value);
  return idx >= 0 ? idx + 1 : 1;
});

const totalInUnit = computed(() => unitLessons.value.length);

const completedInUnit = computed(() => {
  return unitLessons.value.filter((l) => completedLessonIds.value.has(l.id)).length;
});

const unitProgressPct = computed(() => {
  if (totalInUnit.value === 0) return 0;
  return Math.round((completedInUnit.value / totalInUnit.value) * 100);
});

const prevLesson = computed(() => {
  const idx = unitLessons.value.findIndex((l) => l.id === lessonId.value);
  return idx > 0 ? unitLessons.value[idx - 1] : null;
});

const nextLesson = computed(() => {
  const idx = unitLessons.value.findIndex((l) => l.id === lessonId.value);
  return idx >= 0 && idx < unitLessons.value.length - 1 ? unitLessons.value[idx + 1] : null;
});

const nextLessonHref = computed(() => {
  if (!nextLesson.value) return '';
  return `/learn/${courseId.value}/lesson/${nextLesson.value.id}`;
});

const isDirectVideo = computed(() => {
  const url = lesson.value?.videoUrl;
  if (!url) return false;
  return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
});

const embeddedVideoUrl = computed(() => {
  const url = lesson.value?.videoUrl;
  if (!url) return '';
  // YouTube watch URL → embed
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
});

async function fetchData() {
  loading.value = true;
  error.value = null;

  try {
    const [enrollmentsRes, progressRes] = await Promise.all([
      api.get('/learn/enrollments'),
      api.get(`/learn/progress/${courseId.value}`),
    ]);

    const enrollment = enrollmentsRes.data.find(
      (e: { courseId: string }) => e.courseId === courseId.value,
    );

    if (!enrollment) {
      error.value = 'No estás inscripto en este curso';
      loading.value = false;
      return;
    }

    const slug = enrollment.course?.slug;
    if (!slug) {
      error.value = 'No se pudo determinar el curso';
      loading.value = false;
      return;
    }

    const courseRes = await api.get(`/courses/${slug}`);
    const courseData: CourseData = courseRes.data;
    courseTitle.value = courseData.title;

    let foundLesson: LessonData | null = null;
    let foundUnit: UnitData | null = null;

    for (const unit of courseData.units) {
      const match = unit.lessons.find((l) => l.id === lessonId.value);
      if (match) {
        foundUnit = unit;
        foundLesson = match as unknown as LessonData;
        break;
      }
    }

    if (!foundLesson || !foundUnit) {
      error.value = 'Lección no encontrada';
      loading.value = false;
      return;
    }

    lesson.value = foundLesson;
    unitTitle.value = foundUnit.title;
    unitLessons.value = foundUnit.lessons;

    const progressData = progressRes.data;
    const pMap = progressData.progressMap || {};
    const completedSet = new Set<string>();
    for (const [lid, entry] of Object.entries(pMap)) {
      if ((entry as { completed: boolean }).completed) {
        completedSet.add(lid);
      }
    }
    completedLessonIds.value = completedSet;
    isCompleted.value = completedSet.has(lessonId.value);
  } catch (err) {
    error.value = 'Error al cargar la lección';
    console.error(err);
  } finally {
    loading.value = false;
  }
}

async function handleComplete() {
  completing.value = true;
  try {
    const { data } = await api.post('/learn/complete', { lessonId: lessonId.value });

    isCompleted.value = true;
    completedLessonIds.value = new Set([...completedLessonIds.value, lessonId.value]);

    $q.notify({
      message: '¡Lección completada!',
      color: 'positive',
      icon: 'check_circle',
      position: 'top',
      timeout: 3000,
    });

    if (nextLesson.value) {
      setTimeout(() => {
        router.push(nextLessonHref.value);
      }, 1200);
    }
  } catch (err) {
    $q.notify({
      message: 'Error al completar la lección',
      color: 'negative',
      icon: 'error',
      position: 'top',
    });
    console.error(err);
  } finally {
    completing.value = false;
  }
}

onMounted(fetchData);

watch(() => lessonId.value, () => {
  fetchData();
});
</script>

<style scoped lang="scss">
.lesson-page {
  padding: 24px;

  @media (max-width: 640px) {
    padding: 16px;
  }
}

.lesson-container {
  max-width: 820px;
  margin: 0 auto;
}

// === Skeleton ===
.skeleton-breadcrumb {
  margin-bottom: 24px;
}

.skeleton-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.skeleton-content {
  border-radius: var(--oa-radius-xl) !important;
}

// === Error ===
.error-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 64px 32px;
  text-align: center;
}

.error-text {
  font-size: var(--oa-text-md);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

// === Breadcrumb ===
.lesson-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.breadcrumb-link {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  text-decoration: none;
  transition: color var(--oa-duration-fast) ease;

  &:hover {
    color: var(--oa-gold-full);
  }
}

.breadcrumb-sep {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-disabled);
}

.breadcrumb-unit {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
}

.breadcrumb-current {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-secondary);
  font-weight: 500;
}

// === Cover ===
.lesson-cover {
  position: relative;
  margin-bottom: 24px;
  border-radius: var(--oa-radius-xl);
  overflow: hidden;
  aspect-ratio: 21 / 9;
  max-height: 320px;
  background: var(--oa-bg-2);
  box-shadow: 0 4px 12px rgba(13, 43, 69, 0.1);
}

.lesson-cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.lesson-cover-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 50%,
    rgba(13, 43, 69, 0.25) 100%
  );
  pointer-events: none;
}

// === Header ===
.lesson-header {
  margin-bottom: 32px;
}

.lesson-header-top {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.lesson-number-badge {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--oa-gold-full) 18%, transparent) 0%,
    color-mix(in srgb, var(--oa-gold-full) 8%, transparent) 100%
  );
  color: var(--oa-gold-full);
  font-weight: 700;
  font-size: var(--oa-text-md);
  flex-shrink: 0;
  border: 1px solid color-mix(in srgb, var(--oa-gold-full) 22%, transparent);
}

.lesson-header-info {
  flex: 1;
  min-width: 0;
}

.lesson-eyebrow {
  font-size: var(--oa-text-xs);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--oa-gold-full);
  font-weight: 600;
  margin: 0 0 6px 0;
}

.lesson-title {
  font-size: var(--oa-text-xl);
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
  line-height: 1.25;
  font-family: var(--oa-font-display, var(--oa-font-body));
}

.lesson-summary {
  margin: 16px 0 0 58px;
  padding: 14px 18px;
  background: color-mix(in srgb, var(--oa-bg-2) 70%, transparent);
  border-left: 3px solid var(--oa-gold-full);
  border-radius: 0 var(--oa-radius-md) var(--oa-radius-md) 0;
  font-size: var(--oa-text-base);
  color: var(--oa-text-secondary);
  line-height: 1.6;
  font-style: italic;

  @media (max-width: 640px) {
    margin-left: 0;
  }
}

.lesson-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  margin-left: 58px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    margin-left: 0;
  }
}

.badge-gold {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: color-mix(in srgb, var(--oa-gold-full) 14%, transparent);
  color: var(--oa-gold-full);
}

.badge-completed {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  background: color-mix(in srgb, var(--oa-success) 14%, transparent);
  color: var(--oa-success);
}

.meta-type {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.meta-time {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  display: flex;
  align-items: center;
  gap: 4px;
}

.unit-progress-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  margin-left: 58px;

  @media (max-width: 640px) {
    margin-left: 0;
  }
}

.unit-progress-track {
  flex: 1;
  height: 6px;
  background: color-mix(in srgb, var(--oa-bg-3) 50%, transparent);
  border-radius: 3px;
  overflow: hidden;
}

.unit-progress-fill {
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, var(--oa-gold-mid), var(--oa-gold-full));
  transition: width 0.5s ease;
}

.unit-progress-text {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  flex-shrink: 0;
  font-weight: 500;
}

// === Featured video ===
.lesson-video {
  margin-bottom: 28px;
}

.lesson-video-frame {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--oa-radius-xl);
  overflow: hidden;
  background: var(--oa-bg-3);
  border: 1px solid color-mix(in srgb, var(--oa-border) 60%, transparent);
  box-shadow: 0 4px 14px rgba(13, 43, 69, 0.1);
}

.lesson-video-el,
.lesson-video-iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

.lesson-video-caption {
  margin: 8px 4px 0;
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

// === Content ===
.content-card {
  background: var(--oa-bg-2);
  border-radius: var(--oa-radius-xl);
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(13, 43, 69, 0.04);
}

// === Attachments ===
.attachments-section {
  margin-top: 24px;
  padding: 18px 22px;
  background: var(--oa-bg-2);
  border-radius: var(--oa-radius-lg);
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
}

.attachments-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  font-size: var(--oa-text-sm);
  font-weight: 700;
  color: var(--oa-text-primary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.attachments-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.attachment-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: var(--oa-radius-md);
  text-decoration: none;
  color: var(--oa-text-primary);
  font-size: var(--oa-text-sm);
  background: var(--oa-bg-1);
  border: 1px solid transparent;
  transition: all var(--oa-duration-fast) ease;

  &:hover {
    border-color: color-mix(in srgb, var(--oa-gold-full) 25%, transparent);
    background: color-mix(in srgb, var(--oa-gold-full) 4%, var(--oa-bg-1));
  }
}

.attachment-ext {
  margin-left: auto;
  color: var(--oa-text-muted);
}

// === Complete ===
.complete-section {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.completed-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--oa-text-sm);
  font-weight: 600;
  color: var(--oa-success);
}

// === Navigation ===
.nav-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 32px;
  padding-top: 16px;
  border-top: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: var(--oa-radius-lg);
  font-size: var(--oa-text-sm);
  color: var(--oa-text-secondary);
  text-decoration: none;
  transition: all var(--oa-duration-fast) ease;

  &:hover {
    background: color-mix(in srgb, var(--oa-bg-3) 30%, transparent);
    color: var(--oa-text-primary);
  }
}

.nav-arrow {
  font-size: 18px;
  transition: transform var(--oa-duration-fast) ease;
}

.nav-prev:hover .nav-arrow {
  transform: translateX(-2px);
}

.nav-next:hover .nav-arrow {
  transform: translateX(2px);
}

.nav-label-full {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 640px) {
    display: none;
  }
}

.nav-label-short {
  display: none;

  @media (max-width: 640px) {
    display: inline;
  }
}
</style>
